const {
    filter,
    findLast,
    get,
    isEmpty,
    merge,
    replace,
} = require('lodash');

const SUBSCRIPTION_PROCESSING_STATE_ID = 'SubscriptionProcessing';

/**
 * Delivery Saver Payment Setup Component.
 * Note 1: Avoid adding payment type specific concerns in this component.
 *         i.e. This component does not need to know about specific payment instrument types (e.g. giftCard or Paypal)
 *         All payment type specific logic should be part of the instruments component.
 */
class DeliverySaverPaymentSetupController {
    constructor($element, $q, $state, $filter, $scope, settings, announcerService, digitalPayPaymentErrorDisplayService, loadingIndicatorService, digitalPayIframeEventHandler, digitalPayPaymentDistributionService, digitalPayPrepaymentActionsService, digitalPayDisablePaymentStateService, digitalPayPaymentErrorStateService, digitalPayErrorMessageGenerator, deliverySaverService, shopperService, dialogRegistry, analytics) {
        this.$element = $element;
        this.$q = $q;
        this.$state = $state;
        this.$filter = $filter;
        this.$scope = $scope;
        this.settings = settings;
        this.announcerService = announcerService;
        this.digitalPayPaymentErrorDisplayService = digitalPayPaymentErrorDisplayService;
        this.loadingIndicatorService = loadingIndicatorService;
        this.iframeEventHandler = digitalPayIframeEventHandler;
        this.paymentDistributionService = digitalPayPaymentDistributionService;
        this.prePaymentActionsService = digitalPayPrepaymentActionsService;
        this.disablePaymentStateService = digitalPayDisablePaymentStateService;
        this.digitalPayPaymentErrorStateService = digitalPayPaymentErrorStateService;
        this.digitalPayErrorMessageGenerator = digitalPayErrorMessageGenerator;
        this.deliverySaverService = deliverySaverService;
        this.shopperService = shopperService;
        this.dialogRegistry = dialogRegistry;
        this.analytics = analytics;
    }

    $onInit() {
        this.isInitialised = false;
        this.agreeToTncs = false;
        this.agreeToSubscriptionTncs = false;
        this.isCvvDialogEnabled = false;
        this.iframeEventHandler.init();
        this.prePaymentActionsService.init();
        this.disablePaymentStateService.init();
        this.digitalPayPaymentErrorStateService.init();
        this.digitalPayErrorTitle = 'Sorry, we\'ve encountered an issue.';
        this.dialog = {
            PLAN_NOTMATCH: 'SUBSCRIPTION_PLAN_NOT_MATCH',
        };

        this.prePaymentActionDefinitions = {
            addAction: this.prePaymentActionsService.addAction.bind(this.prePaymentActionsService),
            removeAction: this.prePaymentActionsService.removeAction.bind(this.prePaymentActionsService),
        };

        this.paymentStateDefinitions = {
            addDisablePaymentState: this.disablePaymentStateService.addDisablePaymentState.bind(this.disablePaymentStateService),
            removeDisablePaymentState: this.disablePaymentStateService.removeDisablePaymentState.bind(this.disablePaymentStateService),
        };

        this.deliverySaverService.getPlan(this.$state.params.planId).then((response) => {
            this.plan = response.Plan;
            this.verifyPlan(this.plan);

            if (this.settings.deliverySaver.paymentSetupDisclaimerText) {
                const billingFrequency = this.$filter('deliverySaverBillingFrequency')(this.plan);
                this.paymentSetupDisclaimerTextHtml = this.settings.deliverySaver.paymentSetupDisclaimerText.replace('{{ BillingFrequency }}', billingFrequency);
            }

            this.isInitialised = true;
        });
    }

    $onDestroy() {
        this.digitalPayPaymentErrorDisplayService.clearGlobalError();
        this.iframeEventHandler.dispose();
        this.prePaymentActionsService.removeAllActions();
        this.digitalPayPaymentErrorStateService.clearErrors();
    }

    /**
     * Notifies when child instrument components are in manage mode.
     * @param {*boolean} state
     */
    notifyWhenChildIsInManageMode(state) {
        // The field here is an additional field used for showing the account password textbox
        this.anyChildIsInManageMode = state;
    }

    shouldDisablePayment() {
        return this.disablePaymentStateService.hasDisablePaymentStates()
            || !this.paymentDistributionService.hasSingleSelectType()
            || !this.agreeToTncs
            || !this.agreeToSubscriptionTncs
            || !this.billingAddress;
    }

    subscribeToDeliverySaver() {
        // logic from digital-pay-button
        this.digitalPayPaymentErrorDisplayService.clearGlobalError();

        if (this.shouldDisablePayment()) {
            this.announcerService.announce(replace(this.settings.digitalPayScreenReaderMessages.placeOrderInvalidDisableMessage, '{0}', this.disablePaymentStateService.getReasonsWhyDisabled()));
            return;
        }

        this.paymentStateDefinitions.addDisablePaymentState(SUBSCRIPTION_PROCESSING_STATE_ID, this.settings.digitalPayScreenReaderMessages.subscriptionProcessingDisableMessage);
        this.resetPaymentStates();
        const prePaymentActions = this.prePaymentActionsService.getAllActions();

        const prePaymentPromises = [];
        prePaymentActions.forEach((action) => {
            prePaymentPromises.push(action());
        });

        const subscribePromise = paymentInstrument => this.deliverySaverService
            .subscribe(this.$state.params.planId, paymentInstrument)
            .then((data) => {
                // Update shopperService.shopper.DeliveryPlusSubscriptionStatus
                this.shopperService.getCurrentShopperDetails();

                this.$state.go('shop.deliveryplus.confirmation', { data });
            })
            .catch(response => this.handleSubscribeFailure(response));

        this.loadingIndicatorService.startLoading(true, 'Processing your payment', false);

        // Once all prepayment promises are resolved make the payment.
        this.$q.all(prePaymentPromises)
            .then(prePaymentResults => this.getPaymentInstrument(prePaymentResults))
            .then(subscribePromise)
            .catch((error) => {
                this.hasPrePaymentActionFailures = true;
                if (error && error.errorMessage) {
                    this.digitalPayPaymentErrorDisplayService.pushGlobalError(error.errorMessage);
                }
            })
            .finally(() => {
                this.loadingIndicatorService.finishLoading();
                this.paymentStateDefinitions.removeDisablePaymentState(SUBSCRIPTION_PROCESSING_STATE_ID);
            });
    }

    /**
     * Returns a paymentInstrument object
     * which is a merging of all the results for the instrument being used
     * @param {Promise[]} prePaymentResults
     * @returns {Object} paymentInstrument with paymentInstrumentId and stepUpToken
     */
    getPaymentInstrument(prePaymentResults) {
        let paymentInstrumentId = this.paymentDistributionService.getSingleSelectKey();
        if (this.paymentDistributionService.isPayingWithNewCard()) {
            // There should only be one prePaymentResult
            // but double check for a stepUpToken to ensure we use the right one
            paymentInstrumentId = get(findLast(prePaymentResults, result => result.paymentInstrumentId && result.stepUpToken), 'paymentInstrumentId');
        }
        if (paymentInstrumentId != null) {
            const paymentInstrumentResults = filter(prePaymentResults, result => result.paymentInstrumentId === paymentInstrumentId);
            const paymentInstrument = merge(...paymentInstrumentResults);
            if (!isEmpty(paymentInstrument)) {
                return paymentInstrument;
            }
        }
        return this.$q.reject({ errorMessage: this.settings.deliverySaver.paymentSetupGenericError });
    }

    /**
     * For clearing errors and resetting prepayment states
     */
    resetPaymentStates() {
        this.digitalPayPaymentErrorDisplayService.clearGlobalError();
        this.hasPrePaymentActionFailures = false;
        this.hasPrePaymentValidationFailure = false;
        this.shouldRemoveNewlyAddedInstruments = false;
        this.digitalPayErrorMessageGenerator.setPaymentErrorMode(false);
    }

    handleSubscribeFailure(response) {
        let errorMessage = this.settings.deliverySaver.paymentSetupGenericError;
        if (response.status === 400 && response.data.Errors) {
            const errorCodes = response.data.Errors.map(error => error.ErrorCode);
            if (errorCodes.includes(this.settings.subscriptionsErrorCodes.alreadySubscribed)) {
                errorMessage = this.settings.deliverySaver.paymentSetupAlreadySubscribedError;
            } else if (errorCodes.includes(this.settings.subscriptionsErrorCodes.planNotFound)) {
                errorMessage = this.settings.deliverySaver.paymentSetupPlanNotFoundError;
            }
        } else if (response.status === 504) {
            errorMessage = this.settings.deliverySaver.paymentSetupTimeoutError;
        } else if (response.status >= 500) {
            errorMessage = this.settings.deliverySaver.paymentSetupServerError;
        }
        this.digitalPayPaymentErrorDisplayService.pushGlobalError(errorMessage);
        this.shouldRemoveNewlyAddedInstruments = true;
    }

    verifyPlan(plan) {
        // if guest user, skip plan validation, login require will be validated after
        if (this.shopperService.IsGuest) return;

        const shopper = this.shopperService.shopper;

        const isBusinessPlan = plan.Metadata.IsBusinessToBusiness;
        const isBusinessAccount = !!(shopper.IsBusinessAccount || shopper.ABN);

        if (isBusinessAccount === isBusinessPlan) {
            return;
        } else if (isBusinessAccount) {
            this.planAccountTypeValidationFailedTitle = this.settings.deliverySaver.planAccountTypeValidationFailedTitleBusinessAccount;
            this.planAccountTypeValidationFailedText = this.settings.deliverySaver.planAccountTypeValidationFailedTextBusinessAccount;
            this.planAccountTypeValidationContinueButtonText = this.settings.deliverySaver.planAccountTypeValidationContinueButtonTextBusinessAccount;
            this.planAccountTypeValidationChangeAccountText = this.settings.deliverySaver.planAccountTypeValidationChangeAccountTextBusinessAccount;
        } else {
            this.planAccountTypeValidationFailedTitle = this.settings.deliverySaver.planAccountTypeValidationFailedTitlePersonalAccount;
            this.planAccountTypeValidationFailedText = this.settings.deliverySaver.planAccountTypeValidationFailedTextPersonalAccount;
            this.planAccountTypeValidationContinueButtonText = this.settings.deliverySaver.planAccountTypeValidationContinueButtonTextPersonalAccount;
            this.planAccountTypeValidationChangeAccountText = this.settings.deliverySaver.planAccountTypeValidationChangeAccountTextPersonalAccount;
        }

        this.planNotMatchDialog = this.dialogRegistry.getDialog(this.dialog.PLAN_NOTMATCH);
        this.planNotMatchDialog.show();
    }

    onInnerLinkClicked(event) {
        if (event.target.nodeName.toLowerCase() === 'a') {
            this.analytics.push({
                data: {
                    event: {
                        category: 'Delivery Plus',
                        label: `DSS: ${event.target.innerText}`,
                        name: 'link_click',
                    },
                    trackingType: 'link',
                },
                type: this.analytics.mapperTypes.customEvent.customEvent,
            });
        }
    }
}

DeliverySaverPaymentSetupController.$inject = ['$element', '$q', '$state', '$filter', '$scope', 'Settings', 'AnnouncerService', 'DigitalPayPaymentErrorDisplayService', 'LoadingIndicatorService', 'DigitalPayIframeEventHandler', 'DigitalPayPaymentDistributionService', 'DigitalPayPrepaymentActionsService', 'DigitalPayDisablePaymentStateService', 'DigitalPayPaymentErrorStateService', 'DigitalPayErrorMessageGenerator', 'DeliverySaverService', 'ShopperService', 'DialogRegistry', 'AnalyticsService'];

const DeliverySaverPaymentSetupComponentDefinition = {
    controller: DeliverySaverPaymentSetupController,
    template: require('./delivery-saver-payment-setup.component.html').trim(),
};

module.exports = {
    DeliverySaverPaymentSetupController,
    DeliverySaverPaymentSetupComponentDefinition,
};



// WEBPACK FOOTER //
// ./src/deliverysaver/paymentsetup/delivery-saver-payment-setup.component.js